import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { FiX } from 'react-icons/fi';
import axios from 'axios';
import Loader from 'react-loader-spinner';
import { BASE_URL } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import { fetchAdminLeagueStructure, fetchLeagueTeams } from '../actions/leaguesActions';
import { buildLeagueStructure } from '../utilities/leagueStructure';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/modal.css';
import '../styles/addPlayerToApplication.css';

// Добавление игрока в заявку команды (страница игрока).
// Команду ищут двумя способами:
//  - по названию — среди всех команд админа (/teams/getAdminTeams);
//  - по турниру — чемпионат → лига → сезон, затем состав этого сезона
//    (/leagues/:id/teams). Нужен админам с сотнями команд и одинаковыми названиями.
// Если команду нашли через сезон, заявка этого сезона (league_settings.application_id)
// выбирается сама — её можно поменять.

const LAST_PICK_KEY = 'addPlayerToApplication.lastPick';
const NO_CHAMPIONAT = 'none';

// Последний выбор: админы обычно добавляют игроков в одну и ту же лигу подряд
const readLastPick = () => {
    try {
        const raw = localStorage.getItem(LAST_PICK_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

const writeLastPick = (pick) => {
    try {
        localStorage.setItem(LAST_PICK_KEY, JSON.stringify(pick));
    } catch (e) {
        // приватный режим браузера — просто не запоминаем
    }
};

// getAdminTeams склеивает teams.client_owner и teams_owners — одна команда может прийти дважды
const dedupeTeams = (list) => {
    const seen = new Set();
    return (Array.isArray(list) ? list : []).filter(team => {
        const id = Number(team.id);
        if (Number.isNaN(id) || seen.has(id)) return false;
        seen.add(id);
        return true;
    });
};

// Команда из состава сезона (/leagues/:id/teams) в том же виде, что из getAdminTeams
const normalizeSeasonTeam = (team) => ({
    ...team,
    id: Number(team.team_id !== undefined ? team.team_id : team.id),
    name: team.name || team.team_name || 'Без названия',
    short_name: team.short_name || '',
    image: team.image || '',
});

const pickRows = (data) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.data)) return data.data;
    if (data && Array.isArray(data.teams)) return data.teams;
    return [];
};

// Сезон по умолчанию: активный сезон лиги, иначе самый новый
const defaultSeasonId = (league) => {
    const seasons = (league && league.seasons) || [];
    if (!seasons.length) return '';
    const active = seasons.find(season => Number(season.season_id) === Number(league.active_season));
    if (active) return String(active.season_id);
    const newest = seasons.reduce(
        (best, season) => (Number(season.season_id) > Number(best.season_id) ? season : best),
        seasons[0]
    );
    return String(newest.season_id);
};

const matchesQuery = (team, query) => (
    (team.name || '').toLowerCase().includes(query)
    || (team.short_name || '').toLowerCase().includes(query)
);

const teamImage = (image) => {
    if (!image) return defaultTeamLogo;
    return image.startsWith('/') ? BASE_URL.url + image : image;
};

export default function AddPlayerToApplicationModal({ isOpen, onClose, onSuccess, playerId }) {
    const dispatch = useDispatch();

    const [teams, setTeams] = useState([]);
    const [applications, setApplications] = useState([]);

    const [mode, setMode] = useState(() => ((readLastPick() || {}).mode === 'tournament' ? 'tournament' : 'name'));
    const [searchTeam, setSearchTeam] = useState('');
    const [selectedTeam, setSelectedTeam] = useState('');
    const [selectedApplication, setSelectedApplication] = useState('');
    const [playerNumber, setPlayerNumber] = useState('');
    const [occupiedNumbers, setOccupiedNumbers] = useState([]);

    // Поиск по турниру
    const [structure, setStructure] = useState(null);
    const [structureLoading, setStructureLoading] = useState(false);
    const [structureError, setStructureError] = useState('');
    const [championatId, setChampionatId] = useState('');
    const [leagueId, setLeagueId] = useState('');
    const [seasonId, setSeasonId] = useState('');
    const [seasonTeams, setSeasonTeams] = useState([]);
    const [seasonTeamsLoading, setSeasonTeamsLoading] = useState(false);
    const [seasonTeamsError, setSeasonTeamsError] = useState('');
    const [seasonFilter, setSeasonFilter] = useState('');

    const [loading, setLoading] = useState(false);
    const [loadingApplications, setLoadingApplications] = useState(false);
    const [isAdding, setIsAdding] = useState(false);

    // Ответы на устаревшие запросы (сменили сезон или команду) не применяем
    const seasonRequestRef = useRef(0);
    const applicationsRequestRef = useRef(0);
    const suggestedApplicationRef = useRef(null);

    const activeLeague = structure && leagueId ? structure.leaguesById[Number(leagueId)] : null;

    // Заявка выбранного сезона — её предлагаем, если команду нашли через сезон
    const seasonApplicationId = useMemo(() => {
        if (mode !== 'tournament' || !activeLeague || !seasonId) return null;
        const settings = activeLeague.settings ? activeLeague.settings[Number(seasonId)] : null;
        return settings && settings.application_id ? Number(settings.application_id) : null;
    }, [mode, activeLeague, seasonId]);
    suggestedApplicationRef.current = seasonApplicationId;

    useEffect(() => {
        if (isOpen) {
            resetForm();
            loadTeams();
        }
    }, [isOpen]);

    // Чемпионаты грузим, только когда понадобился поиск по турниру
    useEffect(() => {
        if (isOpen && mode === 'tournament' && !structure && !structureLoading && !structureError) {
            loadStructure();
        }
    }, [isOpen, mode, structure, structureLoading, structureError]);

    useEffect(() => {
        if (leagueId && seasonId) {
            loadSeasonTeams(leagueId, seasonId);
        } else {
            seasonRequestRef.current += 1;
            setSeasonTeams([]);
            setSeasonTeamsError('');
            setSeasonTeamsLoading(false);
        }
    }, [leagueId, seasonId]);

    useEffect(() => {
        setSelectedApplication('');
        setOccupiedNumbers([]);
        if (selectedTeam !== '') {
            loadApplications(selectedTeam);
        } else {
            applicationsRequestRef.current += 1;
            setApplications([]);
            setLoadingApplications(false);
        }
    }, [selectedTeam]);

    useEffect(() => {
        if (selectedApplication) {
            loadOccupiedNumbers(selectedApplication);
        } else {
            setOccupiedNumbers([]);
        }
    }, [selectedApplication]);

    // Режим и выбранный турнир сохраняются между открытиями модалки
    const resetForm = () => {
        setSearchTeam('');
        setSeasonFilter('');
        setSelectedTeam('');
        setSelectedApplication('');
        setPlayerNumber('');
        setOccupiedNumbers([]);
        setApplications([]);
    };

    const authHeaders = async () => {
        if (checkIfTokenExpired()) {
            await dispatch(refreshTokens());
        }
        const tokens = getTokensFromStore();
        return {
            "Authorization": "Bearer " + tokens.access_token,
            'Content-Type': 'application/json',
        };
    };

    const loadTeams = async () => {
        try {
            setLoading(true);
            const headers = await authHeaders();
            const response = await axios.post(BASE_URL.url + '/teams/getAdminTeams', {}, { headers });
            const teamsData = response.data?.data || response.data || [];
            setTeams(dedupeTeams(teamsData));
        } catch (error) {
            console.error('Error loading teams:', error);
            alert('Ошибка загрузки команд');
        } finally {
            setLoading(false);
        }
    };

    const loadApplications = async (teamId) => {
        const requestId = ++applicationsRequestRef.current;
        try {
            setLoadingApplications(true);
            const headers = await authHeaders();
            const response = await axios.post(
                BASE_URL.url + '/teams/getAdminApplications',
                { team_id: teamId },
                { headers }
            );
            if (requestId !== applicationsRequestRef.current) return;
            const appsData = response.data?.applications || response.data?.data || response.data || [];
            const list = Array.isArray(appsData) ? appsData : [];
            setApplications(list);
            // Команду нашли через сезон — сразу ставим заявку этого сезона
            const suggested = suggestedApplicationRef.current;
            if (suggested && list.some(app => Number(app.application_id) === suggested)) {
                setSelectedApplication(String(suggested));
            }
        } catch (error) {
            if (requestId !== applicationsRequestRef.current) return;
            console.error('Error loading applications:', error);
            alert('Ошибка загрузки заявок');
        } finally {
            if (requestId === applicationsRequestRef.current) setLoadingApplications(false);
        }
    };

    const loadOccupiedNumbers = async (applicationId) => {
        try {
            const headers = await authHeaders();
            const response = await axios.get(
                BASE_URL.url + `/teams/${selectedTeam}/applicationsWithActiveProfiles`,
                { headers }
            );

            let apps = [];
            if (Array.isArray(response.data)) {
                apps = response.data;
            } else if (response.data?.rosters) {
                apps = response.data.rosters;
            } else if (response.data?.applications) {
                apps = response.data.applications;
            } else if (response.data?.data) {
                apps = response.data.data;
            }

            const app = apps.find(a => a.application_id === parseInt(applicationId));
            if (app && app.data && Array.isArray(app.data)) {
                const numbers = app.data
                    .map(player => parseInt(player.player_number))
                    .filter(num => !isNaN(num) && num > 0);
                setOccupiedNumbers(numbers);
            } else {
                setOccupiedNumbers([]);
            }
        } catch (error) {
            console.error('Error loading occupied numbers:', error);
            setOccupiedNumbers([]);
        }
    };

    // --- поиск по турниру ---

    const loadStructure = async () => {
        setStructureLoading(true);
        setStructureError('');
        try {
            const payload = await dispatch(fetchAdminLeagueStructure());
            const built = buildLeagueStructure(payload || { championats: [], orphan_leagues: [] });
            setStructure(built);
            restoreLastPick(built);
        } catch (error) {
            console.error('Error loading league structure:', error);
            setStructureError('Не удалось загрузить чемпионаты');
        } finally {
            setStructureLoading(false);
        }
    };

    const championatOf = (league, built) => (
        league.championat_id !== null && built.tree[league.championat_id]
            ? String(league.championat_id)
            : NO_CHAMPIONAT
    );

    const restoreLastPick = (built) => {
        const last = readLastPick();
        if (!last || !last.leagueId) return;
        const league = built.leaguesById[Number(last.leagueId)];
        if (!league) return;
        const seasonKnown = league.seasons.some(season => String(season.season_id) === String(last.seasonId));
        setChampionatId(championatOf(league, built));
        setLeagueId(String(league.id));
        setSeasonId(seasonKnown ? String(last.seasonId) : defaultSeasonId(league));
    };

    const leaguesOf = (value) => {
        if (!structure || !value) return [];
        return value === NO_CHAMPIONAT ? structure.orphanLeagues : (structure.tree[Number(value)] || []);
    };

    const chooseLeague = (value, champ = championatId) => {
        setLeagueId(value);
        setSelectedTeam('');
        const league = value && structure ? structure.leaguesById[Number(value)] : null;
        const season = league ? defaultSeasonId(league) : '';
        setSeasonId(season);
        if (league) writeLastPick({ mode: 'tournament', championatId: champ, leagueId: value, seasonId: season });
    };

    const chooseChampionat = (value) => {
        setChampionatId(value);
        setSelectedTeam('');
        const leagues = leaguesOf(value);
        // Лига в чемпионате одна — выбираем её сразу
        if (leagues.length === 1) {
            chooseLeague(String(leagues[0].id), value);
        } else {
            setLeagueId('');
            setSeasonId('');
        }
    };

    const chooseSeason = (value) => {
        setSeasonId(value);
        setSelectedTeam('');
        writeLastPick({ mode: 'tournament', championatId, leagueId, seasonId: value });
    };

    const switchMode = (next) => {
        if (next === mode) return;
        setMode(next);
        setSelectedTeam('');
        writeLastPick({ ...(readLastPick() || {}), mode: next });
    };

    const loadSeasonTeams = async (league, season) => {
        const requestId = ++seasonRequestRef.current;
        setSeasonTeamsLoading(true);
        setSeasonTeamsError('');
        setSeasonFilter('');
        try {
            const data = await dispatch(fetchLeagueTeams(league, season));
            if (requestId !== seasonRequestRef.current) return;
            setSeasonTeams(dedupeTeams(pickRows(data).map(normalizeSeasonTeam)));
        } catch (error) {
            if (requestId !== seasonRequestRef.current) return;
            console.error('Error loading season teams:', error);
            setSeasonTeams([]);
            setSeasonTeamsError('Не удалось загрузить состав сезона');
        } finally {
            if (requestId === seasonRequestRef.current) setSeasonTeamsLoading(false);
        }
    };

    const championatOptions = useMemo(() => {
        if (!structure) return [];
        const list = structure.championats.map(ch => ({ id: String(ch.id), name: ch.name }));
        if (structure.orphanLeagues.length) list.push({ id: NO_CHAMPIONAT, name: 'Без чемпионата' });
        return list;
    }, [structure]);

    const leagueOptions = useMemo(() => leaguesOf(championatId), [structure, championatId]);

    // Новые сезоны сверху
    const seasonOptions = useMemo(() => (
        activeLeague ? [...activeLeague.seasons].sort((a, b) => Number(b.season_id) - Number(a.season_id)) : []
    ), [activeLeague]);

    // --- списки команд ---

    const nameTeams = useMemo(() => {
        const query = searchTeam.trim().toLowerCase();
        return query ? teams.filter(team => matchesQuery(team, query)) : teams;
    }, [teams, searchTeam]);

    const tournamentTeams = useMemo(() => {
        const query = seasonFilter.trim().toLowerCase();
        return query ? seasonTeams.filter(team => matchesQuery(team, query)) : seasonTeams;
    }, [seasonTeams, seasonFilter]);

    const visibleTeams = mode === 'name' ? nameTeams : tournamentTeams;

    // Одинаковые названия в списке — показываем ID, чтобы их различить
    const nameCounts = useMemo(() => {
        const counts = {};
        visibleTeams.forEach(team => {
            const key = (team.name || '').trim().toLowerCase();
            counts[key] = (counts[key] || 0) + 1;
        });
        return counts;
    }, [visibleTeams]);

    // Данные команды для запроса: из команд админа (там есть client_owner),
    // иначе из состава сезона
    const findTeam = (id) => {
        const numeric = Number(id);
        return teams.find(team => Number(team.id) === numeric)
            || seasonTeams.find(team => Number(team.id) === numeric)
            || null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedTeam === '') {
            alert('Выберите команду');
            return;
        }

        if (!selectedApplication) {
            alert('Выберите заявку');
            return;
        }

        if (!playerNumber || playerNumber.trim() === '') {
            alert('Введите номер игрока');
            return;
        }

        const number = parseInt(playerNumber);
        if (isNaN(number) || number < 0 || number > 99) {
            alert('Номер должен быть от 0 до 99');
            return;
        }

        if (occupiedNumbers.includes(number)) {
            alert(`Номер ${number} уже занят в этой заявке`);
            return;
        }

        try {
            setIsAdding(true);

            const selectedTeamData = findTeam(selectedTeam);
            if (!selectedTeamData) {
                throw new Error('Команда не найдена');
            }

            const headers = await authHeaders();
            await axios.post(
                BASE_URL.url + '/players/connectToApplication',
                {
                    playerId: playerId,
                    add_data: [{
                        id: Number(selectedTeam),
                        application_id: parseInt(selectedApplication),
                        player_number: number,
                        client_owner: selectedTeamData.client_owner
                    }],
                    remove_data: []
                },
                { headers }
            );

            alert('Игрок успешно добавлен в заявку!');
            onSuccess();
            handleClose();
        } catch (error) {
            console.error('Error adding player to application:', error);
            alert('Ошибка добавления игрока: ' + (error?.response?.data?.message || error?.message || 'Неизвестная ошибка'));
        } finally {
            setIsAdding(false);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleNumberChange = (e) => {
        let value = e.target.value.replace(/[^0-9]/g, '');
        if (value.length > 2) {
            value = value.slice(0, 2);
        }
        setPlayerNumber(value);
    };

    if (!isOpen) return null;

    const renderLoader = (text) => (
        <div className="apmLoader">
            <Loader type="Oval" color="#4299e1" height={30} width={30} />
            <span>{text}</span>
        </div>
    );

    // Пустой список, загрузка или ошибка; null — показываем команды
    const renderListState = () => {
        if (mode === 'tournament') {
            if (structureLoading) return renderLoader('Загружаем чемпионаты…');
            if (structureError) {
                return (
                    <div className="apmEmpty">
                        {structureError}.{' '}
                        <button type="button" className="apmLink" onClick={loadStructure}>Повторить</button>
                    </div>
                );
            }
            if (!leagueId || !seasonId) return <div className="apmEmpty">Выберите чемпионат, лигу и сезон</div>;
            if (seasonTeamsLoading) return renderLoader('Загружаем состав сезона…');
            if (seasonTeamsError) {
                return (
                    <div className="apmEmpty">
                        {seasonTeamsError}.{' '}
                        <button type="button" className="apmLink" onClick={() => loadSeasonTeams(leagueId, seasonId)}>Повторить</button>
                    </div>
                );
            }
            if (!seasonTeams.length) return <div className="apmEmpty">В составе сезона нет команд</div>;
            if (!tournamentTeams.length) return <div className="apmEmpty">В этом сезоне таких команд нет</div>;
            return null;
        }
        if (!teams.length) return <div className="apmEmpty">Команд пока нет</div>;
        if (!nameTeams.length) return <div className="apmEmpty">Команды не найдены</div>;
        return null;
    };

    const renderTeamRow = (team) => {
        const active = selectedTeam !== '' && Number(selectedTeam) === Number(team.id);
        const duplicate = nameCounts[(team.name || '').trim().toLowerCase()] > 1;
        const meta = [team.short_name, duplicate ? `ID ${team.id}` : null].filter(Boolean).join(' · ');
        return (
            <button
                type="button"
                key={team.id}
                className={`apmTeam ${active ? 'apmTeamActive' : ''}`}
                aria-pressed={active}
                onClick={() => setSelectedTeam(team.id)}
            >
                <img
                    className="apmTeamLogo"
                    src={teamImage(team.image)}
                    alt=""
                    onError={(e) => {
                        e.target.src = defaultTeamLogo;
                    }}
                />
                <span className="apmTeamText">
                    <span className="apmTeamName">{team.name}</span>
                    {meta && <span className="apmTeamMeta">{meta}</span>}
                </span>
                {active && <span className="apmTeamCheck">✓</span>}
            </button>
        );
    };

    const listState = renderListState();
    const seasonApplicationChosen = seasonApplicationId !== null
        && Number(selectedApplication) === seasonApplicationId;

    return (
        <div className="modalOverlay" onClick={handleClose}>
            <div
                className="modalContent"
                onClick={(e) => e.stopPropagation()}
                style={{ maxHeight: '90vh', maxWidth: '560px', display: 'flex', flexDirection: 'column' }}
            >
                <div className="modalHeader">
                    <h2>Добавить в заявку команды</h2>
                    <button type="button" className="closeButton" onClick={handleClose}>
                        <FiX />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modalBody" style={{ overflowY: 'auto', flex: 1, minHeight: '500px' }}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                            <Loader type="Oval" color="#4299e1" height={50} width={50} />
                        </div>
                    ) : (
                        <>
                            <div className="formGroup">
                                <label>Команда *</label>
                                <div className="apmSwitch" role="tablist" aria-label="Как искать команду">
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={mode === 'name'}
                                        className={`apmSwitchButton ${mode === 'name' ? 'apmSwitchButtonActive' : ''}`}
                                        onClick={() => switchMode('name')}
                                    >
                                        По названию
                                    </button>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={mode === 'tournament'}
                                        className={`apmSwitchButton ${mode === 'tournament' ? 'apmSwitchButtonActive' : ''}`}
                                        onClick={() => switchMode('tournament')}
                                    >
                                        По чемпионату и лиге
                                    </button>
                                </div>

                                {mode === 'name' ? (
                                    <input
                                        type="text"
                                        className="apmSearch"
                                        placeholder="Введите название команды..."
                                        value={searchTeam}
                                        onChange={(e) => setSearchTeam(e.target.value)}
                                    />
                                ) : (
                                    <>
                                        <div className="apmCascade">
                                            <select
                                                className="apmFull"
                                                aria-label="Чемпионат"
                                                value={championatId}
                                                onChange={(e) => chooseChampionat(e.target.value)}
                                                disabled={!structure}
                                            >
                                                <option value="">Чемпионат</option>
                                                {championatOptions.map(ch => (
                                                    <option key={ch.id} value={ch.id}>{ch.name}</option>
                                                ))}
                                            </select>
                                            <select
                                                aria-label="Лига"
                                                value={leagueId}
                                                onChange={(e) => chooseLeague(e.target.value)}
                                                disabled={!championatId}
                                            >
                                                <option value="">Лига</option>
                                                {leagueOptions.map(league => (
                                                    <option key={league.id} value={league.id}>{league.name}</option>
                                                ))}
                                            </select>
                                            <select
                                                aria-label="Сезон"
                                                value={seasonId}
                                                onChange={(e) => chooseSeason(e.target.value)}
                                                disabled={!leagueId}
                                            >
                                                <option value="">Сезон</option>
                                                {seasonOptions.map(season => (
                                                    <option key={season.season_id} value={season.season_id}>
                                                        {season.season_name}
                                                        {activeLeague && Number(activeLeague.active_season) === Number(season.season_id) ? ' · активный' : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {seasonTeams.length > 8 && (
                                            <input
                                                type="text"
                                                className="apmSearch"
                                                placeholder="Найти команду в этом сезоне..."
                                                value={seasonFilter}
                                                onChange={(e) => setSeasonFilter(e.target.value)}
                                            />
                                        )}
                                    </>
                                )}

                                <div className="apmList">
                                    {listState || visibleTeams.map(renderTeamRow)}
                                </div>
                            </div>

                            {selectedTeam !== '' && (
                                <div className="formGroup">
                                    <label>Заявка *</label>
                                    {loadingApplications ? (
                                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                                            <Loader type="Oval" color="#4299e1" height={30} width={30} />
                                        </div>
                                    ) : (
                                        <select
                                            value={selectedApplication}
                                            onChange={(e) => setSelectedApplication(e.target.value)}
                                            required
                                            disabled={applications.length === 0}
                                        >
                                            <option value="">Выберите заявку</option>
                                            {applications.map(app => {
                                                const year = app.application_year || app.year;
                                                const isSeason = seasonApplicationId !== null
                                                    && Number(app.application_id) === seasonApplicationId;
                                                return (
                                                    <option key={app.application_id} value={app.application_id}>
                                                        {app.application_name}
                                                        {year ? ` (${year})` : ''}
                                                        {isSeason ? ' — заявка сезона' : ''}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    )}
                                    {!loadingApplications && applications.length === 0 && (
                                        <p style={{ fontSize: '13px', color: '#e53e3e', margin: '8px 0 0 0' }}>
                                            У команды нет заявок
                                        </p>
                                    )}
                                    {!loadingApplications && seasonApplicationChosen && (
                                        <p className="apmHint">Выбрана заявка этого сезона — при необходимости её можно поменять</p>
                                    )}
                                </div>
                            )}

                            {selectedApplication && (
                                <div className="formGroup">
                                    <label>Номер игрока *</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={2}
                                        value={playerNumber}
                                        onChange={handleNumberChange}
                                        placeholder="Введите номер (0-99)"
                                        required
                                    />
                                    {occupiedNumbers.length > 0 && (
                                        <p style={{ fontSize: '12px', color: '#718096', margin: '8px 0 0 0' }}>
                                            Занятые номера: {[...occupiedNumbers].sort((a, b) => a - b).join(', ')}
                                        </p>
                                    )}
                                </div>
                            )}

                            <div className="modalActions">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="cancelButton"
                                    disabled={isAdding}
                                >
                                    Отмена
                                </button>
                                <button
                                    type="submit"
                                    className="submitButton"
                                    disabled={isAdding || selectedTeam === '' || !selectedApplication || !playerNumber}
                                >
                                    {isAdding ? (
                                        <>
                                            <Loader type="Oval" color="#fff" height={16} width={16} />
                                            <span style={{ marginLeft: '8px' }}>Добавление...</span>
                                        </>
                                    ) : (
                                        'Добавить в заявку'
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
}
